import { Route } from 'vue-router';
import Pusher from 'pusher-js';

const pusher = new Pusher(import.meta.env.VITE_PUSHER_KEY as string, {
  cluster: import.meta.env.VITE_PUSHER_CLUSTER as string,
});

export const usePusher = () => {
  /**
   * This module manages reloading the app so that our client software is always up-to-date.
   * Several scenarios exist:
   *  - Periodic - once a day
   *  - On remote request (push signal)
   *
   * We use deferred "soft" reloading to minimize disruptions to the user. When a deferred reload
   * is requested, the app will wait for the next navigation event, i.e. when the user transitions
   * from one view to another. In this case, the effect of reloading will be minimal.
   *
   * To initiate reloading, a push signal is sent on each release. It can contain either a
   * deferred or immediate reload request. If immediate reload is required, the app will trigger
   * hard reload of the browser page at once. Otherwise, it will wait for the next navigation event.
   *
   * In addition, we ensure reloading the app periodically (currently once a day). This method uses
   * deferred reloading.
   *
   */

  // const PERIODIC_RELOAD_FLAG = 'cfs_periodic_reload_scheduled';
  const DEFERRED_RELOAD_FLAG = 'cfs_deferred_reload_requested';

  const SignalType = Object.freeze({
    IMMEDIATE: 'immediate',
    DEFERRED: 'deferred',
  });

  const getLocalStorage = (key: string) => {
    const localStorageItem = localStorage.getItem(key);
    return localStorageItem ? JSON.parse(localStorageItem) : null;
  };
  const setLocalStorage = (key: string, value: unknown) =>
    localStorage.setItem(key, JSON.stringify(value));

  /**
   * Reload the page if deferred reload was requested.
   * This method is designed to be called when a navigation event is detected.
   */
  const ensureDeferredReload = (to: Route) => {
    if (getLocalStorage(DEFERRED_RELOAD_FLAG)) {
      setLocalStorage(DEFERRED_RELOAD_FLAG, false);

      //@ts-ignore
      window.location = '#' + to.fullPath;
    }
  };

  /**
   * Setting cookies for 1-day reload, reloading page if didn't find cookie then
   * creating new cookie for 1-day
   */
  const ensurePeriodicReload = () => {
    // console.log('ensurePeriodicReload');
    // const { cookies } = useCookies();
    // if (!cookies.isKey(PERIODIC_RELOAD_FLAG)) {
    //   cookies.set(PERIODIC_RELOAD_FLAG, '', '1d');
    // Disabled for now until we test this better
    // window.location = to.fullPath
    // }
  };

  /**
   * Subscribe to action Pusher
   */
  const initializePusher = () => {
    const channel = pusher.subscribe('deployment');

    channel.bind('new_release', (fetchedType: string) => {
      if (import.meta.env.DEV) {
        return;
      }
      switch (fetchedType) {
        case SignalType.IMMEDIATE:
          window.location.reload();
          break;
        case SignalType.DEFERRED:
        default:
          setLocalStorage(DEFERRED_RELOAD_FLAG, true);
      }
    });
  };

  const ensureAppReload = (to: Route) => {
    ensurePeriodicReload();
    ensureDeferredReload(to);
  };

  return { ensureAppReload, initializePusher };
};
